import { useEffect, useState } from "react";
import axios from "axios";
import xmlJs from "xml-js";
import {
  ArrowLeft,
  Image as ImageIcon,
  Video,
  FileText,
  BarChart3,
  Download,
  Sparkles,
  BookOpen,
  Search,
  X,
  ExternalLink,
  Building2,
  CheckCircle2,
  Loader2,
  Check,
  Copy,
} from "lucide-react";
import { Modal } from "antd";
import { masterCaseStudies } from "./caseStudiesData";
import { brandLogosData } from "./logosData";
import { videosData } from "./videosData";
import "../Styles/Assets.scss";

// Cross-Origin Safe Stream Downloader with State Management
const triggerBlobDownload = async (url, fileName, assetId, setDownloadStates) => {
  if (!url) return;
  setDownloadStates((prev) => ({ ...prev, [assetId]: "downloading" }));
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Fetch failed");
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || "asset";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    setDownloadStates((prev) => ({ ...prev, [assetId]: "completed" }));
    setTimeout(() => {
      setDownloadStates((prev) => {
        const next = { ...prev };
        delete next[assetId];
        return next;
      });
    }, 2000);
  } catch (err) {
    console.warn("Direct blob stream fallback to anchor:", err);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "asset";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setDownloadStates((prev) => ({ ...prev, [assetId]: "completed" }));
    setTimeout(() => {
      setDownloadStates((prev) => {
        const next = { ...prev };
        delete next[assetId];
        return next;
      });
    }, 2000);
  }
};

// 1-Click Clipboard Link Copier with Tooltip State
const copyAssetLink = async (url, assetId, setCopyStates) => {
  try {
    const targetUrl = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    await navigator.clipboard.writeText(targetUrl);
    setCopyStates((prev) => ({ ...prev, [assetId]: true }));
    setTimeout(() => {
      setCopyStates((prev) => ({ ...prev, [assetId]: false }));
    }, 1800);
  } catch (err) {
    console.error("Clipboard copy failed:", err);
  }
};

// Exact SVG double quote icon matching design system
const CustomQuoteIcon = ({ size = 18, color = "#F05742" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 8a2.5 2.5 0 1 0 0 5h1a1 1 0 0 0 1-1V9.5a1.5 1.5 0 0 0-1.5-1.5H7zm0 5c0 2.8-1.5 4.5-3.5 5.5" />
    <path d="M17 8a2.5 2.5 0 1 0 0 5h1a1 1 0 0 0 1-1V9.5a1.5 1.5 0 0 0-1.5-1.5H17zm0 5c0 2.8-1.5 4.5-3.5 5.5" />
  </svg>
);

const Assets = () => {
  const [activeCategory, setActiveCategory] = useState(null); // null | 'logos' | 'videos' | 'leaflets' | 'decks' | 'casestudies'

  return (
    <div className="apple-assets-wrapper">
      {activeCategory === null ? (
        <div className="apple-assets-grid-view">
          {/* Header Tagline */}
          <div className="apple-assets-header-meta">
            <span className="apple-assets-tag">PARTNER ASSETS</span>
            <h2 className="apple-assets-title">Marketing & Brand Resources</h2>
            <p className="apple-assets-sub">
              Access official iNextLabs logos, videos, brochures, pitch decks, and verified case studies.
            </p>
          </div>

          {/* Apple Ceramic Cards Grid */}
          <div className="apple-assets-card-grid">
            {/* Featured Hero Card: Case Studies Library */}
            <div
              className="apple-asset-nav-card featured-card"
              onClick={() => setActiveCategory("casestudies")}
            >
              <div className="apple-asset-icon-stage featured-stage">
                <BookOpen size={26} />
              </div>
              <div className="featured-card-content">
                <div className="featured-top-row">
                  <h3 className="apple-asset-card-title">Case Studies & Outcomes</h3>
                  <span className="featured-counter-pill">14 Stories</span>
                </div>
                <p className="apple-asset-card-sub">
                  Verified customer success benchmarks across Singapore, Malaysia, and India.
                </p>
                <span className="apple-asset-link">Explore case library →</span>
              </div>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("logos")}
            >
              <div className="apple-asset-icon-stage">
                <ImageIcon size={26} />
              </div>
              <h3 className="apple-asset-card-title">Logos</h3>
              <p className="apple-asset-card-sub">Official vector & high-res brand marks</p>
              <span className="apple-asset-link">Browse logos →</span>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("videos")}
            >
              <div className="apple-asset-icon-stage">
                <Video size={26} />
              </div>
              <h3 className="apple-asset-card-title">Videos</h3>
              <p className="apple-asset-card-sub">Product walkthroughs & demo clips</p>
              <span className="apple-asset-link">Browse videos →</span>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("leaflets")}
            >
              <div className="apple-asset-icon-stage">
                <FileText size={26} />
              </div>
              <h3 className="apple-asset-card-title">Brochures</h3>
              <p className="apple-asset-card-sub">Product leaflets & solution briefs</p>
              <span className="apple-asset-link">Browse brochures →</span>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("decks")}
            >
              <div className="apple-asset-icon-stage">
                <BarChart3 size={26} />
              </div>
              <h3 className="apple-asset-card-title">Decks</h3>
              <p className="apple-asset-card-sub">Partner sales & pitch presentations</p>
              <span className="apple-asset-link">Browse decks →</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="apple-assets-category-view">
          {/* Top Breadcrumb Navigation */}
          <div className="apple-assets-nav-bar">
            <button
              type="button"
              className="apple-assets-back-pill"
              onClick={() => setActiveCategory(null)}
            >
              <ArrowLeft size={16} />
              <span>Back to Assets</span>
            </button>
            <span className="apple-assets-category-tag">
              {activeCategory.toUpperCase()}
            </span>
          </div>

          {/* Inline Active Asset View */}
          <div className="apple-assets-view-body">
            {activeCategory === "casestudies" && <CaseStudiesAssetView />}
            {activeCategory === "logos" && <Logos />}
            {activeCategory === "videos" && <Videos />}
            {activeCategory === "leaflets" && <Leaflets />}
            {activeCategory === "decks" && <Decks />}
          </div>
        </div>
      )}
    </div>
  );
};

/* ==========================================================================
   GLOBAL CASE STUDIES ASSET VIEW (ALL 14 CASES WITH COUNTRY & SEARCH FILTERS)
   ========================================================================== */
const CaseStudiesAssetView = () => {
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeStory, setActiveStory] = useState(null);

  const countries = ["All", "Singapore", "Malaysia", "India"];

  const filteredStories = masterCaseStudies.filter((item) => {
    const matchesCountry =
      selectedCountry === "All" ||
      item.country.toLowerCase() === selectedCountry.toLowerCase();
    const matchesSearch =
      searchQuery.trim() === "" ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.industry.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.product.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCountry && matchesSearch;
  });

  return (
    <div className="apple-assets-casestudies-view">
      {/* Top Filter & Search Bar */}
      <div className="apple-assets-filter-row">
        <div className="apple-country-pills">
          {countries.map((c) => (
            <button
              key={c}
              type="button"
              className={`apple-country-pill ${selectedCountry === c ? "active" : ""}`}
              onClick={() => setSelectedCountry(c)}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="apple-assets-search-wrap">
          <Search size={14} className="search-icon" />
          <input
            type="text"
            placeholder="Search stories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="apple-assets-search-input"
          />
        </div>
      </div>

      {/* Case Studies List / Grid */}
      {filteredStories.length === 0 ? (
        <div className="apple-assets-empty-state">
          <div className="empty-state-icon-stage">
            <Search size={24} />
          </div>
          <h4 className="empty-state-title">No case studies found</h4>
          <p className="empty-state-desc">
            We couldn't find any stories matching "{searchQuery}".
          </p>
          <button
            type="button"
            className="empty-state-reset-btn"
            onClick={() => {
              setSearchQuery("");
              setSelectedCountry("All");
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="apple-assets-casestudy-list" data-lenis-prevent="true">
          {filteredStories.map((story) => (
            <div
              key={story.id}
              className="apple-asset-casestudy-card ios-touch-item"
              onClick={() => setActiveStory(story)}
            >
              {story.image && (
                <div className="casestudy-card-thumb-wrap">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="casestudy-card-thumb"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <div className="casestudy-card-overlay" />
                  <span className="casestudy-card-product-badge">
                    {story.product}
                  </span>
                  {story.stat && (
                    <span className="casestudy-card-stat-pill">
                      <Sparkles size={11} />
                      {story.stat}
                    </span>
                  )}
                </div>
              )}
              <div className="casestudy-card-info">
                <div className="casestudy-card-meta">
                  <span className="casestudy-card-tag">{story.tag}</span>
                </div>
                <h4 className="casestudy-card-title">{story.title}</h4>
                <p className="casestudy-card-desc">"{story.text}"</p>
                <div className="casestudy-card-action">
                  <span className="casestudy-read-btn">
                    Explore Case Overview →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* In-App Apple Spatial Case Study Modal */}
      {activeStory && (
        <Modal
          open={Boolean(activeStory)}
          onCancel={() => setActiveStory(null)}
          footer={null}
          centered={typeof window !== "undefined" && window.innerWidth >= 768}
          width={700}
          className="apple-spatial-casestudy-modal"
          closeIcon={
            <div className="apple-modal-close-pill" aria-label="Close">
              <X size={16} />
            </div>
          }
        >
          <div className="casestudy-modal-content" data-lenis-prevent="true">
            {/* iOS Native Sheet Grabber Bar (Mobile) */}
            <div className="ios-sheet-grabber">
              <div className="ios-sheet-grabber-pill" />
            </div>

            {activeStory.image && (
              <div className="casestudy-modal-hero-wrap">
                <img
                  src={activeStory.image}
                  alt={activeStory.title}
                  className="casestudy-modal-hero-img"
                  loading="lazy"
                />
                <div className="casestudy-modal-hero-overlay" />
                {activeStory.stat && (
                  <span className="casestudy-modal-stat-pill">
                    <Sparkles size={13} />
                    {activeStory.stat}
                  </span>
                )}
              </div>
            )}

            {/* Scrollable Content Body (Only text & details scroll) */}
            <div className="casestudy-modal-scroll-body" data-lenis-prevent="true">
              <div className="casestudy-modal-inner">
                <div className="casestudy-modal-meta-row">
                  {activeStory.tag && (
                    <span className="casestudy-modal-tag">{activeStory.tag}</span>
                  )}
                  <span className="casestudy-modal-client">
                    <Building2 size={13} color="#F05742" />
                    {activeStory.client}
                  </span>
                </div>

                <h3 className="casestudy-modal-title">{activeStory.title}</h3>

                <div className="casestudy-modal-quote-box">
                  <div className="modal-quote-icon">
                    <CustomQuoteIcon size={20} color="#F05742" />
                  </div>
                  <p className="modal-quote-text">{activeStory.text}</p>
                </div>

                {activeStory.outcomes && (
                  <div className="casestudy-modal-highlights">
                    <h5 className="modal-highlights-title">Key Verified Outcomes</h5>
                    <div className="modal-highlights-list">
                      {activeStory.outcomes.map((outcome, idx) => (
                        <div key={idx} className="modal-highlight-item">
                          <CheckCircle2 size={16} color="#10b981" />
                          <span>{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sticky Footer CTA & Actions */}
              <div className="casestudy-modal-footer">
                {activeStory.url && (
                  <a
                    href={activeStory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="casestudy-modal-primary-btn"
                  >
                    <span>Read Full Story on inextlabs.ai</span>
                    <ExternalLink size={14} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setActiveStory(null)}
                  className="casestudy-modal-secondary-btn"
                >
                  Close Overview
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Logos = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [downloadStates, setDownloadStates] = useState({});
  const [copyStates, setCopyStates] = useState({});

  const categories = ["All", "Brand", "DocsAI", "EngageAI", "InsightsAI"];

  const filteredLogos = brandLogosData.filter((logo) => {
    const matchesCategory =
      selectedCategory === "All" ||
      logo.category.toLowerCase() === selectedCategory.toLowerCase();
    const matchesSearch =
      searchQuery.trim() === "" ||
      logo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      logo.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      logo.variant.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="apple-assets-logos-section">
      {/* Category Filter Pills & Search Bar */}
      <div className="apple-assets-filter-bar">
        <div className="apple-country-pills">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`apple-country-pill ${
                selectedCategory === cat ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === "All" ? "All Logos" : cat}
              <span className="pill-count">
                {cat === "All"
                  ? brandLogosData.length
                  : brandLogosData.filter(
                      (l) => l.category.toLowerCase() === cat.toLowerCase()
                    ).length}
              </span>
            </button>
          ))}
        </div>

        <div className="apple-assets-search-wrap">
          <Search size={15} className="search-icon" />
          <input
            type="text"
            placeholder="Search logo marks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="apple-assets-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={() => setSearchQuery("")}
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Grid of 8 Official Master Logos */}
      {filteredLogos.length === 0 ? (
        <div className="apple-assets-empty-state">
          <div className="empty-state-icon-stage">
            <Search size={24} />
          </div>
          <h4 className="empty-state-title">No logo marks found</h4>
          <p className="empty-state-desc">
            We couldn't find any logo marks matching "{searchQuery}".
          </p>
          <button
            type="button"
            className="empty-state-reset-btn"
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("All");
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="apple-assets-logos-grid" data-lenis-prevent="true">
          {filteredLogos.map((logo) => (
            <div key={logo.id} className="apple-asset-logo-card ios-touch-item">
              {/* Logo Preview Canvas (Dark stage for White Logos, Light stage for Black Logos) */}
              <div
                className={`apple-asset-logo-stage ${
                  logo.isDarkBg ? "dark-stage" : "light-stage"
                }`}
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="apple-asset-logo-preview-img"
                />
                <span className="apple-logo-theme-badge">
                  {logo.isDarkBg ? "Dark Theme / White" : "Light Theme / Black"}
                </span>
              </div>

              {/* Logo Meta & Actions */}
              <div className="apple-asset-logo-card-info">
                <div className="apple-asset-logo-card-meta">
                  <span className="apple-asset-logo-tag">{logo.category}</span>
                  <span className="apple-asset-logo-format-badge">
                    {logo.format}
                  </span>
                </div>
                <h4 className="apple-asset-logo-card-title">{logo.name}</h4>
                <p className="apple-asset-logo-card-desc">{logo.description}</p>

                <div className="apple-asset-logo-card-action">
                  <button
                    type="button"
                    className={`apple-asset-download-btn ${
                      downloadStates[logo.id] === "completed"
                        ? "is-completed"
                        : downloadStates[logo.id] === "downloading"
                        ? "is-downloading"
                        : ""
                    }`}
                    onClick={() =>
                      triggerBlobDownload(
                        logo.src,
                        logo.fileName,
                        logo.id,
                        setDownloadStates
                      )
                    }
                    disabled={downloadStates[logo.id] === "downloading"}
                    aria-label={`Download ${logo.name}`}
                  >
                    {downloadStates[logo.id] === "downloading" ? (
                      <>
                        <Loader2 size={15} className="apple-btn-spin" />
                        <span>Downloading...</span>
                      </>
                    ) : downloadStates[logo.id] === "completed" ? (
                      <>
                        <Check size={15} color="#10b981" />
                        <span className="saved-text">Saved!</span>
                      </>
                    ) : (
                      <>
                        <Download size={15} />
                        <span>Download</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className={`apple-asset-copy-btn ${
                      copyStates[logo.id] ? "is-copied" : ""
                    }`}
                    title={copyStates[logo.id] ? "Link Copied!" : "Copy Asset URL"}
                    aria-label={`Copy link for ${logo.name}`}
                    onClick={() => copyAssetLink(logo.src, logo.id, setCopyStates)}
                  >
                    {copyStates[logo.id] ? (
                      <Check size={14} color="#10b981" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Videos = () => {
  const [selectedProduct, setSelectedProduct] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [downloadStates, setDownloadStates] = useState({});
  const [copyStates, setCopyStates] = useState({});

  const products = ["All", "DocsAI", "EngageAI"];

  const filteredVideos = videosData.filter((video) => {
    const matchesProduct =
      selectedProduct === "All" ||
      video.product.toLowerCase() === selectedProduct.toLowerCase();
    const matchesSearch =
      searchQuery.trim() === "" ||
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesProduct && matchesSearch;
  });

  return (
    <div className="apple-assets-videos-section">
      {/* Category Filter Pills & Search Bar */}
      <div className="apple-assets-filter-bar">
        <div className="apple-country-pills">
          {products.map((prod) => (
            <button
              key={prod}
              type="button"
              className={`apple-country-pill ${
                selectedProduct === prod ? "active" : ""
              }`}
              onClick={() => setSelectedProduct(prod)}
            >
              {prod === "All" ? "All Videos" : prod}
              <span className="pill-count">
                {prod === "All"
                  ? videosData.length
                  : videosData.filter(
                      (v) => v.product.toLowerCase() === prod.toLowerCase()
                    ).length}
              </span>
            </button>
          ))}
        </div>

        <div className="apple-assets-search-wrap">
          <Search size={15} className="search-icon" />
          <input
            type="text"
            placeholder="Search demo videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="apple-assets-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={() => setSearchQuery("")}
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Video Cards Grid / List */}
      {filteredVideos.length === 0 ? (
        <div className="apple-assets-empty-state">
          <div className="empty-state-icon-stage">
            <Search size={24} />
          </div>
          <h4 className="empty-state-title">No videos found</h4>
          <p className="empty-state-desc">
            We couldn't find any videos matching "{searchQuery}".
          </p>
          <button
            type="button"
            className="empty-state-reset-btn"
            onClick={() => {
              setSearchQuery("");
              setSelectedProduct("All");
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="apple-assets-videos-list" data-lenis-prevent="true">
          {filteredVideos.map((video) => (
            <div key={video.id} className="apple-asset-video-card ios-touch-item">
              <div className="apple-asset-video-player-wrap">
                <video
                  src={video.src}
                  className="apple-asset-video-player"
                  controls
                  preload="metadata"
                  playsInline
                />
              </div>

              <div className="apple-asset-video-card-info">
                <div className="apple-asset-video-card-meta">
                  <span className="apple-asset-video-tag">{video.product}</span>
                  <span className="apple-asset-video-format-badge">
                    {video.format}
                  </span>
                </div>
                <h4 className="apple-asset-video-card-title">{video.title}</h4>
                <p className="apple-asset-video-card-desc">
                  {video.description}
                </p>

                <div className="apple-asset-video-card-action">
                  <button
                    type="button"
                    className={`apple-asset-download-btn ${
                      downloadStates[video.id] === "completed"
                        ? "is-completed"
                        : downloadStates[video.id] === "downloading"
                        ? "is-downloading"
                        : ""
                    }`}
                    onClick={() =>
                      triggerBlobDownload(
                        video.src,
                        video.fileName,
                        video.id,
                        setDownloadStates
                      )
                    }
                    disabled={downloadStates[video.id] === "downloading"}
                    aria-label={`Download ${video.title}`}
                  >
                    {downloadStates[video.id] === "downloading" ? (
                      <>
                        <Loader2 size={15} className="apple-btn-spin" />
                        <span>Downloading...</span>
                      </>
                    ) : downloadStates[video.id] === "completed" ? (
                      <>
                        <Check size={15} color="#10b981" />
                        <span className="saved-text">Saved!</span>
                      </>
                    ) : (
                      <>
                        <Download size={15} />
                        <span>Download</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className={`apple-asset-copy-btn ${
                      copyStates[video.id] ? "is-copied" : ""
                    }`}
                    title={copyStates[video.id] ? "Link Copied!" : "Copy Video URL"}
                    aria-label={`Copy link for ${video.title}`}
                    onClick={() => copyAssetLink(video.src, video.id, setCopyStates)}
                  >
                    {copyStates[video.id] ? (
                      <Check size={14} color="#10b981" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Leaflets = () => {
  const [leafletsData, setLeafletsData] = useState(null);
  const [downloadStates, setDownloadStates] = useState({});
  const [fetchError, setFetchError] = useState(false);

  const fetchLeaflets = async () => {
    setFetchError(false);
    try {
      const response = await axios.get(
        "https://inextlabswebassets.blob.core.windows.net/partners-demo-assets-brochures?restype=container&comp=list"
      );
      const xmlData = response.data;
      const jsonData = xmlJs.xml2json(xmlData, { compact: true, spaces: 2 });
      const blobs = JSON.parse(jsonData).EnumerationResults.Blobs.Blob;
      setLeafletsData(Array.isArray(blobs) ? blobs : [blobs]);
    } catch (error) {
      console.error("Error fetching leaflets data:", error);
      setFetchError(true);
    }
  };

  useEffect(() => {
    fetchLeaflets();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      {fetchError ? (
        <div className="apple-assets-empty-state">
          <h4 className="empty-state-title">Unable to load brochures</h4>
          <p className="empty-state-desc">
            Please check your network connection and try again.
          </p>
          <button
            type="button"
            className="empty-state-reset-btn"
            onClick={fetchLeaflets}
          >
            Retry Loading
          </button>
        </div>
      ) : leafletsData ? (
        <div className="apple-assets-list">
          {leafletsData.map((leaflet, index) => {
            const fileName = leaflet.Name._text;
            const fileUrl = leaflet.Url._text;
            const itemKey = `leaflet-${index}`;
            return (
              <div key={index} className="apple-asset-item-card">
                <div className="apple-asset-media-box pdf-box">
                  <FileText size={44} color="#F05742" />
                </div>
                <div className="apple-asset-item-info">
                  <h4 className="apple-asset-item-name">
                    {fileName.split(".")[0]}
                  </h4>
                  <p className="apple-asset-item-date">
                    Updated {formatDate(leaflet.Properties["Last-Modified"]._text)}
                  </p>

                  <button
                    type="button"
                    className={`apple-asset-download-btn ${
                      downloadStates[itemKey] === "completed"
                        ? "is-completed"
                        : downloadStates[itemKey] === "downloading"
                        ? "is-downloading"
                        : ""
                    }`}
                    onClick={() =>
                      triggerBlobDownload(
                        fileUrl,
                        fileName,
                        itemKey,
                        setDownloadStates
                      )
                    }
                    disabled={downloadStates[itemKey] === "downloading"}
                    aria-label={`Download ${fileName}`}
                  >
                    {downloadStates[itemKey] === "downloading" ? (
                      <>
                        <Loader2 size={14} className="apple-btn-spin" />
                        <span>Downloading...</span>
                      </>
                    ) : downloadStates[itemKey] === "completed" ? (
                      <>
                        <Check size={14} color="#10b981" />
                        <span className="saved-text">Saved!</span>
                      </>
                    ) : (
                      <>
                        <Download size={14} />
                        <span>Download</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="apple-assets-loading">
          <Sparkles className="spin-icon" size={24} color="#F05742" />
          <span>Loading brochures...</span>
        </div>
      )}
    </div>
  );
};

const Decks = () => {
  const [decksData, setDecksData] = useState(null);
  const [downloadStates, setDownloadStates] = useState({});
  const [fetchError, setFetchError] = useState(false);

  const fetchDecks = async () => {
    setFetchError(false);
    try {
      const response = await axios.get(
        "https://inextlabswebassets.blob.core.windows.net/partners-demo-assets-decks?restype=container&comp=list"
      );
      const xmlData = response.data;
      const jsonData = xmlJs.xml2json(xmlData, { compact: true, spaces: 2 });
      const blobs = JSON.parse(jsonData).EnumerationResults.Blobs.Blob;
      setDecksData(Array.isArray(blobs) ? blobs : [blobs]);
    } catch (error) {
      console.error("Error fetching deck data:", error);
      setFetchError(true);
    }
  };

  useEffect(() => {
    fetchDecks();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      {fetchError ? (
        <div className="apple-assets-empty-state">
          <h4 className="empty-state-title">Unable to load pitch decks</h4>
          <p className="empty-state-desc">
            Please check your network connection and try again.
          </p>
          <button
            type="button"
            className="empty-state-reset-btn"
            onClick={fetchDecks}
          >
            Retry Loading
          </button>
        </div>
      ) : decksData ? (
        <div className="apple-assets-list">
          {decksData.map((deck, index) => {
            const fileName = deck.Name._text;
            const fileUrl = deck.Url._text;
            const itemKey = `deck-${index}`;
            return (
              <div key={index} className="apple-asset-item-card">
                <div className="apple-asset-media-box ppt-box">
                  <BarChart3 size={44} color="#F05742" />
                </div>
                <div className="apple-asset-item-info">
                  <h4 className="apple-asset-item-name">{fileName.split(".")[0]}</h4>
                  <p className="apple-asset-item-date">
                    Updated {formatDate(deck.Properties["Last-Modified"]._text)}
                  </p>
                  <button
                    type="button"
                    className={`apple-asset-download-btn ${
                      downloadStates[itemKey] === "completed"
                        ? "is-completed"
                        : downloadStates[itemKey] === "downloading"
                        ? "is-downloading"
                        : ""
                    }`}
                    onClick={() =>
                      triggerBlobDownload(
                        fileUrl,
                        fileName,
                        itemKey,
                        setDownloadStates
                      )
                    }
                    disabled={downloadStates[itemKey] === "downloading"}
                    aria-label={`Download ${fileName}`}
                  >
                    {downloadStates[itemKey] === "downloading" ? (
                      <>
                        <Loader2 size={14} className="apple-btn-spin" />
                        <span>Downloading...</span>
                      </>
                    ) : downloadStates[itemKey] === "completed" ? (
                      <>
                        <Check size={14} color="#10b981" />
                        <span className="saved-text">Saved!</span>
                      </>
                    ) : (
                      <>
                        <Download size={14} />
                        <span>Download</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="apple-assets-loading">
          <Sparkles className="spin-icon" size={24} color="#F05742" />
          <span>Loading decks...</span>
        </div>
      )}
    </div>
  );
};

export default Assets;
